import React, { useState, useEffect } from 'react';
import { FiLink, FiEye, FiEyeOff, FiTrash2, FiPlus, FiCopy, FiBarChart2, FiX, FiCheck, FiDownload, FiDroplet, FiClock, FiUsers, FiLock, FiFileText, FiInbox, FiRotateCcw, FiShare2, FiGlobe } from 'react-icons/fi';
import ClientSubmissions from './ClientSubmissions';
import { buildVariantUrl } from '../utils/photoCache';

export default function ShareManager({ albumId, albumTitle }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [selectedShareAnalytics, setSelectedShareAnalytics] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [viewingSubmissions, setViewingSubmissions] = useState(null);

  const [newShare, setNewShare] = useState({
    expires_at: '',
    allow_downloads: false,
    watermark: false,
    theme: 'auto',
    client_mode_enabled: false,
    client_password: '',
    client_instructions: '',
    allow_multiple_submissions: false
  });

  useEffect(() => {
    loadShares();
  }, [albumId]);

  async function loadShares() {
    try {
      const response = await fetch(`/api/shares?album_id=${albumId}`, { credentials: 'include' });
      if (!response.ok) {
        setShares([]);
        return;
      }
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      setShares(list);
      // Nothing to manage yet, jump straight to the create form so sharing is one step.
      if (!list.some(s => !s.revoked)) setShowCreateForm(true);
    } catch (error) {
      console.error('Failed to load shares:', error);
      setShares([]);
    } finally {
      setLoading(false);
    }
  }

  async function createShare() {
    try {
      const response = await fetch('/api/shares', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          album_id: albumId,
          expires_at: newShare.expires_at || null,
          allow_downloads: newShare.allow_downloads,
          watermark: newShare.watermark,
          theme: newShare.theme,
          client_mode_enabled: newShare.client_mode_enabled,
          client_password: newShare.client_mode_enabled ? newShare.client_password : null,
          client_instructions: newShare.client_mode_enabled ? newShare.client_instructions : null,
          allow_multiple_submissions: newShare.allow_multiple_submissions
        })
      });

      if (response.ok) {
        const created = await response.json().catch(() => null);
        await loadShares();
        setShowCreateForm(false);
        setNewShare({
          expires_at: '',
          allow_downloads: false,
          watermark: false,
          theme: 'auto',
          client_mode_enabled: false,
          client_password: '',
          client_instructions: '',
          allow_multiple_submissions: false
        });
        // Copy the fresh link right away (highlights its row via copiedId); fails silently.
        if (created?.id) copyLink(created);
      } else {
          const data = await response.json().catch(() => ({}));
          console.error('Failed to create share:', response.status, data);
          alert(data.message || data.error || `Failed to create share (${response.status})`);
        }
    } catch (error) {
      console.error('Failed to create share:', error);
      alert('Failed to create share: ' + error.message);
    }
  }

  async function toggleRevoke(shareId, currentRevoked) {
    const endpoint = currentRevoked ? 'reinstate' : 'revoke';
    try {
      await fetch(`/api/shares/${shareId}/${endpoint}`, {
        method: 'POST',
        credentials: 'include'
      });
      await loadShares();
    } catch (error) {
      console.error(`Failed to ${endpoint} share:`, error);
    }
  }

  async function deleteShare(shareId) {
    if (!confirm('Archive this share link? You can reinstate it later with the same URL.')) return;
    try {
      await fetch(`/api/shares/${shareId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      await loadShares();
    } catch (error) {
      console.error('Failed to delete share:', error);
    }
  }

  async function loadDetailedAnalytics(shareId) {
    try {
      const response = await fetch(`/api/shares/${shareId}/analytics`, { credentials: 'include' });
      const data = await response.json();
      // Show modal even on 403 (upgrade prompt)
      setAnalytics(data);
      setSelectedShareAnalytics(shareId);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  }

  function copyLink(share) {
    // Use client URL for client mode shares, otherwise use public share URL
    const url = share.client_mode_enabled
      ? `${window.location.origin}/client/${share.id}`
      : `${window.location.origin}/share/${share.token}`;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        setCopiedId(share.id);
        setTimeout(() => setCopiedId(null), 2000);
      }).catch(() => fallbackCopy(url, share.id));
    } else {
      fallbackCopy(url, share.id);
    }
  }

  function fallbackCopy(url, shareId) {
    const textArea = document.createElement('textarea');
    textArea.value = url;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      setCopiedId(shareId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
    document.body.removeChild(textArea);
  }

  function formatDate(date) {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString();
  }

  function formatDuration(seconds) {
    if (!seconds) return '0s';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  }

  function formatSourceLabel(source) {
    const labels = {
      direct: 'Direct / Bookmarks',
      social: 'Social Media',
      email: 'Email',
      search: 'Search Engines',
      other: 'Other Websites',
      unknown: 'Unknown'
    };
    return labels[source] || source;
  }

  if (loading) {
    return <div className="share-loading">Loading...</div>;
  }

  const activeShares = shares.filter(s => !s.revoked);
  const revokedShares = shares.filter(s => s.revoked);

  return (
    <div className="share-manager-clean">
      {/* Create Form or New Share Button */}
      {!showCreateForm ? (
        <button
          onClick={() => setShowCreateForm(true)}
          className="share-new-btn"
        >
          <FiPlus size={18} />
          <span>Create Share Link</span>
        </button>
      ) : (
        <div className="share-create-form-clean">
          <div className="share-form-title">New Share Link</div>

          <div className="share-form-options">
            <label className="share-option">
              <input
                type="checkbox"
                checked={newShare.allow_downloads}
                onChange={(e) => setNewShare({ ...newShare, allow_downloads: e.target.checked })}
              />
              <FiDownload size={16} />
              <span>Allow downloads</span>
            </label>

            <label className="share-option">
              <input
                type="checkbox"
                checked={newShare.watermark}
                onChange={(e) => setNewShare({ ...newShare, watermark: e.target.checked })}
              />
              <FiDroplet size={16} />
              <span>Add watermark</span>
            </label>
          </div>

          <div className="share-form-divider">
            <label className="share-option client-mode-toggle">
              <input
                type="checkbox"
                checked={newShare.client_mode_enabled}
                onChange={(e) => setNewShare({ ...newShare, client_mode_enabled: e.target.checked })}
              />
              <FiUsers size={16} />
              <span>Client Mode (allow photo selections)</span>
            </label>
          </div>

          {newShare.client_mode_enabled && (
            <div className="client-mode-options">
              <div className="share-form-field">
                <label><FiLock size={14} /> Client Password</label>
                <input
                  type="password"
                  value={newShare.client_password}
                  onChange={(e) => setNewShare({ ...newShare, client_password: e.target.value })}
                  placeholder="Required for client access"
                />
              </div>
              <div className="share-form-field">
                <label><FiFileText size={14} /> Instructions for Client</label>
                <textarea
                  value={newShare.client_instructions}
                  onChange={(e) => setNewShare({ ...newShare, client_instructions: e.target.value })}
                  placeholder="e.g., Please select your 20 favorite photos..."
                  rows={2}
                />
              </div>
              <label className="share-option">
                <input
                  type="checkbox"
                  checked={newShare.allow_multiple_submissions}
                  onChange={(e) => setNewShare({ ...newShare, allow_multiple_submissions: e.target.checked })}
                />
                <span>Allow multiple submissions</span>
              </label>
            </div>
          )}

          <div className="share-form-fields">
            <div className="share-form-field">
              <label>Expiration</label>
              <input
                type="date"
                value={newShare.expires_at}
                onChange={(e) => setNewShare({ ...newShare, expires_at: e.target.value })}
                placeholder="Never"
              />
            </div>
          </div>

          <div className="share-form-actions">
            <button onClick={createShare} className="button-primary">
              Create Link
            </button>
            <button
              onClick={() => {
                setShowCreateForm(false);
                setNewShare({
                  expires_at: '',
                  allow_downloads: false,
                  watermark: false,
                  theme: 'auto',
                  client_mode_enabled: false,
                  client_password: '',
                  client_instructions: '',
                  allow_multiple_submissions: false
                });
              }}
              className="button-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Existing Shares */}
      {activeShares.length === 0 && !showCreateForm && (
        <p className="share-empty-text">No share links yet. Create one to share this album.</p>
      )}

      {activeShares.length > 0 && (
        <div className="share-list-clean">
          {activeShares.map(share => {
            const fullUrl = share.client_mode_enabled
              ? `${window.location.origin}/client/${share.id}`
              : `${window.location.origin}/share/${share.token}`;
            const isCopied = copiedId === share.id;

            return (
              <div key={share.id} className={`share-item-clean ${share.client_mode_enabled ? 'client-mode' : ''}`}>
                <div className="share-url-row">
                  <input
                    type="text"
                    value={fullUrl}
                    readOnly
                    className="share-url-input"
                    onClick={(e) => e.target.select()}
                  />
                  <button
                    onClick={() => copyLink(share)}
                    className={`share-copy-btn ${isCopied ? 'copied' : ''}`}
                  >
                    {isCopied ? <><FiCheck size={16} /> Copied</> : <><FiCopy size={16} /> Copy</>}
                  </button>
                </div>

                <div className="share-meta-row">
                  <div className="share-badges">
                    {share.client_mode_enabled && (
                      <span className="share-badge client-badge">
                        <FiUsers size={12} /> Client Mode
                      </span>
                    )}
                    {!share.allow_downloads && (
                      <span className="share-badge">No downloads</span>
                    )}
                    {share.watermark && (
                      <span className="share-badge">Watermarked</span>
                    )}
                    {share.expires_at && (
                      <span className="share-badge">
                        <FiClock size={12} /> {formatDate(share.expires_at)}
                      </span>
                    )}
                  </div>

                  <div className="share-actions-row">
                    {share.client_mode_enabled && (
                      <button
                        onClick={() => setViewingSubmissions(share.id)}
                        className="share-action-btn submissions"
                        title="View Submissions"
                      >
                        <FiInbox size={16} />
                      </button>
                    )}
                    <button
                      onClick={() => loadDetailedAnalytics(share.id)}
                      className="share-action-btn"
                      title="Analytics"
                    >
                      <FiBarChart2 size={16} />
                    </button>
                    <button
                      onClick={() => toggleRevoke(share.id, share.revoked)}
                      className="share-action-btn"
                      title="Disable"
                    >
                      <FiEye size={16} />
                    </button>
                    <button
                      onClick={() => deleteShare(share.id)}
                      className="share-action-btn danger"
                      title="Archive"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Revoked Shares */}
      {revokedShares.length > 0 && (
        <div className="revoked-shares-section">
          <div className="revoked-shares-header">
            <FiEyeOff size={14} />
            <span>Revoked Links ({revokedShares.length})</span>
          </div>
          <div className="share-list-clean revoked">
            {revokedShares.map(share => {
              const fullUrl = share.client_mode_enabled
                ? `${window.location.origin}/client/${share.id}`
                : `${window.location.origin}/share/${share.token}`;

              return (
                <div key={share.id} className={`share-item-clean revoked ${share.client_mode_enabled ? 'client-mode' : ''}`}>
                  <div className="share-url-row">
                    <input
                      type="text"
                      value={fullUrl}
                      readOnly
                      className="share-url-input"
                      onClick={(e) => e.target.select()}
                    />
                    <span className="revoked-badge">Revoked</span>
                  </div>

                  <div className="share-meta-row">
                    <div className="share-badges">
                      {share.client_mode_enabled && (
                        <span className="share-badge client-badge">
                          <FiUsers size={12} /> Client Mode
                        </span>
                      )}
                      {share.expires_at && (
                        <span className="share-badge">
                          <FiClock size={12} /> {formatDate(share.expires_at)}
                        </span>
                      )}
                      <span className="share-badge muted">
                        Created {formatDate(share.created_at)}
                      </span>
                    </div>

                    <div className="share-actions-row">
                      <button
                        onClick={() => toggleRevoke(share.id, share.revoked)}
                        className="share-action-btn reinstate"
                        title="Reinstate (same URL)"
                      >
                        <FiRotateCcw size={16} />
                        <span>Reinstate</span>
                      </button>
                      <button
                        onClick={() => deleteShare(share.id)}
                        className="share-action-btn danger"
                        title="Archive"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Analytics Modal */}
      {selectedShareAnalytics && analytics && (
        <div className="modal-overlay" onClick={() => setSelectedShareAnalytics(null)}>
          <div className="modal-content analytics-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Share Analytics</h3>
              <button onClick={() => setSelectedShareAnalytics(null)}>
                <FiX size={20} />
              </button>
            </div>

            {analytics.error ? (
              <div className="analytics-upgrade-prompt">
                <FiBarChart2 size={24} />
                <p>{analytics.message}</p>
              </div>
            ) : (
              <>
                <div className="analytics-stats">
                  <div className="analytics-stat">
                    <span className="stat-value">{analytics.uniqueVisitors || 0}</span>
                    <span className="stat-label">Visitors</span>
                  </div>
                  <div className="analytics-stat">
                    <span className="stat-value">{analytics.totalVisits || 0}</span>
                    <span className="stat-label">Views</span>
                  </div>
                  <div className="analytics-stat">
                    <span className="stat-value">{analytics.totalPhotoViews || 0}</span>
                    <span className="stat-label">Photo Views</span>
                  </div>
                  <div className="analytics-stat">
                    <span className="stat-value">{formatDuration(analytics.avgDuration)}</span>
                    <span className="stat-label">Avg Time</span>
                  </div>
                </div>

                {analytics.ownerVisits > 0 && (
                  <div className="analytics-note">
                    Your own visits ({analytics.ownerVisits}) are excluded from these numbers.
                  </div>
                )}

                {/* Forwarding Indicator, advanced only */}
                {analytics.forwardingIndicators?.likelyForwarded && (
                  <div className="analytics-alert">
                    <FiShare2 size={16} />
                    <span>
                      This link may have been forwarded &mdash; {analytics.forwardingIndicators.uniqueVisitors} unique
                      visitors from {analytics.forwardingIndicators.distinctSources} different sources
                      {analytics.forwardingIndicators.spreadDays > 0 &&
                        ` over ${analytics.forwardingIndicators.spreadDays} day${analytics.forwardingIndicators.spreadDays !== 1 ? 's' : ''}`}
                    </span>
                  </div>
                )}

                {/* Visitor Sources, advanced only */}
                {analytics.visitorSources && analytics.visitorSources.length > 0 && (
                  <div className="analytics-section">
                    <h4>Visitor Sources</h4>
                    <div className="analytics-source-list">
                      {analytics.visitorSources.map(source => (
                        <div key={source.source} className="analytics-source-item">
                          <span className="source-icon">
                            {source.source === 'social' ? <FiShare2 size={14} /> :
                             source.source === 'email' ? <FiFileText size={14} /> :
                             <FiGlobe size={14} />}
                          </span>
                          <span className="source-label">{formatSourceLabel(source.source)}</span>
                          <span className="source-count">{source.visitors} visitor{source.visitors !== 1 ? 's' : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Visit Timeline, advanced only */}
                {analytics.visitTimeline && analytics.visitTimeline.length > 0 && (
                  <div className="analytics-section">
                    <h4>Recent Activity</h4>
                    <div className="analytics-timeline">
                      {analytics.visitTimeline.slice(-14).map(day => (
                        <div key={day.date} className="timeline-bar" title={`${day.date}: ${day.visits} visit${day.visits !== 1 ? 's' : ''}`}>
                          <div
                            className="timeline-fill"
                            style={{
                              height: `${Math.max(4, Math.min(100, (day.visits / Math.max(...analytics.visitTimeline.map(d => d.visits))) * 100))}%`
                            }}
                          />
                          <span className="timeline-label">{new Date(day.date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Photos, both tiers */}
                {analytics.topPhotos && analytics.topPhotos.length > 0 && (
                  <div className="analytics-section">
                    <h4>Most Viewed Photos</h4>
                    <div className="analytics-photos">
                      {analytics.topPhotos.map(photo => (
                        <div key={photo.image_id} className="analytics-photo">
                          <img
                            src={buildVariantUrl({ filename: photo.filename }, 'xl')}
                            alt={photo.title || 'Photo'}
                          />
                          <span>{photo.view_count} views</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upgrade prompt for basic tier */}
                {analytics.analyticsLevel === 'basic' && (
                  <div className="analytics-upgrade-prompt">
                    <p>Upgrade to Business for visitor sources, forwarding detection, and visit timelines.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Client Submissions Modal */}
      {viewingSubmissions && (
        <div className="modal-overlay" onClick={() => setViewingSubmissions(null)}>
          <div className="modal-content submissions-modal" onClick={(e) => e.stopPropagation()}>
            <ClientSubmissions
              shareId={viewingSubmissions}
              onClose={() => setViewingSubmissions(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
